import React from 'react';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';

const sections = [
  {
    title: 'Information we collect',
    body: [
      'We collect information you provide directly, including your name, email address, shipping details, billing details, order history, support messages, and newsletter preferences.',
      'We also collect limited technical information such as device type, browser type, pages viewed, referring pages, approximate location, and interactions with our site. This helps us keep the store reliable and improve the customer experience.',
    ],
  },
  {
    title: 'How we use information',
    body: [
      'We use your information to process orders, provide shipping updates, answer support requests, manage subscriptions, prevent fraud, improve site performance, and send marketing messages when you have opted in.',
      'We do not sell your personal information. When we share information with service providers, it is for practical store operations such as payment processing, fulfillment, analytics, email delivery, and customer support.',
    ],
  },
  {
    title: 'Payments and checkout',
    body: [
      'Payments are handled by secure third-party payment providers. Berleto does not store full credit card numbers on our servers.',
      'Checkout providers may process your payment and related information under their own privacy and security practices.',
    ],
  },
  {
    title: 'Cookies and analytics',
    body: [
      'We use cookies and similar technologies to remember preferences, keep the cart working, understand site traffic, measure campaigns, and improve the store.',
      'You can control cookies through your browser settings. Some features, including checkout and cart behavior, may not work correctly if essential cookies are disabled.',
    ],
  },
  {
    title: 'Your choices',
    body: [
      'You can unsubscribe from marketing emails at any time by using the unsubscribe link in the email.',
      'You may request access, correction, or deletion of your personal information by contacting sales@berleto.com. We may need to retain certain information where required for order records, fraud prevention, tax, accounting, or legal obligations.',
    ],
  },
  {
    title: 'Data retention',
    body: [
      'We keep personal information only as long as reasonably necessary for the purposes described here, unless a longer retention period is required or permitted by law.',
    ],
  },
  {
    title: 'Contact',
    body: [
      'For privacy questions or requests, contact us at sales@berleto.com.',
    ],
  },
];

export function PrivacyPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      <section className="pt-32 pb-16 border-b" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
        <div className="container mx-auto px-5 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-[1fr_280px] gap-10 md:items-end"
          >
            <div>
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Privacy
              </p>
              <h1 className="text-5xl md:text-7xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
                Your data,<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>handled carefully.</span>
              </h1>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '48ch' }}>
                This policy explains what we collect, why we collect it, and how you can reach us about your information.
              </p>
            </div>

            <div className="border-t pt-6" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
              <p className="text-xs tracking-[0.18em] uppercase mb-3" style={{ color: 'rgba(26,42,26,0.4)' }}>
                Last updated
              </p>
              <p className="text-2xl font-light" style={{ color: '#1A2A1A' }}>
                May 20, 2026
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid md:grid-cols-[220px_1fr] gap-12 md:gap-16 items-start">
            <aside className="hidden md:block md:sticky md:top-32">
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                Policy
              </p>
              <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {sections.map((section) => (
                  <a
                    key={section.title}
                    href={`#${section.title.toLowerCase().replaceAll(' ', '-')}`}
                    className="block border-b py-3 text-xs transition-colors"
                    style={{ borderColor: 'rgba(26,42,26,0.08)', color: 'rgba(26,42,26,0.48)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#B8704D'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(26,42,26,0.48)'; }}
                  >
                    {section.title}
                  </a>
                ))}
              </div>
            </aside>

            <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
              {sections.map((section, index) => (
                <motion.section
                  key={section.title}
                  id={section.title.toLowerCase().replaceAll(' ', '-')}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: Math.min(index * 0.04, 0.2), ease: [0.22, 1, 0.36, 1] }}
                  className="grid md:grid-cols-[56px_1fr] gap-5 border-b py-8 md:py-10"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <span className="text-xs font-medium tracking-[0.18em]" style={{ color: '#B8704D' }}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-light mb-5" style={{ color: '#1A2A1A' }}>
                      {section.title}
                    </h2>
                    <div className="space-y-4">
                      {section.body.map((paragraph) => (
                        <p key={paragraph} className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.58)', maxWidth: '72ch' }}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </motion.section>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
